import { SBDAction } from "./SBDAction/SBDAction";
import { SBDOccupation } from "./SBDOccupation/SBDOccupation";
import { hasPositiveDuration } from "@shared/common/utils/luxonHelpers";
import React, { memo, MouseEventHandler, useCallback } from "react";
import { getTrackHeight } from "../core/functions";
import { OccupationsForTrackWithOrder } from "../core/occupations";
import { SBDDisabledTrack } from "./SBDDisabledTrack";
import { useChainHighlightHandler } from "../core/useChainHighlighting";
import { isPassingTrain, TrackOccupation } from "@opstel/query/models/trackOccupation";
import { useDetailPanelData } from "./useDetailPanelData";

interface SBDTrackProps {
  readonly stageWidth: number;
  readonly occupationsForTrack: OccupationsForTrackWithOrder;
  readonly zoomLevel: number;
}

export function SBDTrack({
  stageWidth,
  occupationsForTrack: { track, occupations },
  zoomLevel,
}: SBDTrackProps): React.JSX.Element {
  const deselectShuntingUnit = useChainHighlightHandler((state) => state.deselectShuntingUnit);
  const clearDetailPanelData = useDetailPanelData((state) => state.clearDetailPanelData);
  const handleClick: MouseEventHandler = useCallback(
    (event) => {
      if (event.target !== event.currentTarget) return;
      deselectShuntingUnit();
      clearDetailPanelData();
    },
    [deselectShuntingUnit, clearDetailPanelData],
  );

  return (
    <div
      className="track relative bg-emphasis-primary"
      style={{
        width: `${stageWidth}px`,
        height: `${getTrackHeight(track.maxOrder)}px`,
      }}
      aria-label={`Bezettingen voor ${track.code}`}
      onClick={handleClick}
    >
      {track.blockedDurations.map((blockedDuration) => {
        return (
          <SBDDisabledTrack
            key={`SBDTrack-StyledTrack-SBDDisabledTrack-${blockedDuration.startTime.toUnixInteger()}-${blockedDuration.endTime.toUnixInteger()}`}
            blockedDuration={blockedDuration}
            zoomLevel={zoomLevel}
          />
        );
      })}
      <SBDOccupationsMemo
        occupations={occupations}
        zoomLevel={zoomLevel}
        maxOrder={track.maxOrder}
      />
    </div>
  );
}

const SBDOccupationsMemo = memo(SBDOccupations);

function SBDOccupations({
  occupations,
  zoomLevel,
  maxOrder,
}: {
  readonly occupations: TrackOccupation[];
  readonly zoomLevel: number;
  readonly maxOrder: number;
}) {
  const occupationsWithPositiveDuration = occupations.filter(hasPositiveDuration);

  return (
    <>
      {occupationsWithPositiveDuration.map((occupation) => {
        return (
          <SBDOccupation
            key={`${occupation.shuntingUnit.guid}-${occupation.startTime.toISO()}`}
            occupation={occupation}
            zoomLevel={zoomLevel}
            showStickyText={!isPassingTrain(occupation)}
          >
            <SBDActionsMemo occupation={occupation} zoomLevel={zoomLevel} maxOrder={maxOrder} />
          </SBDOccupation>
        );
      })}
    </>
  );
}

const SBDActionsMemo = memo(SBDActions);

function SBDActions({
  occupation,
  zoomLevel,
  maxOrder,
}: {
  readonly occupation: TrackOccupation;
  readonly zoomLevel: number;
  readonly maxOrder: number;
}) {
  return (
    <>
      {occupation.actions.map((action) => (
        <SBDActionMemo
          key={`SBDTrack-StyledTrack-SBDOccupation-SBDAction-${action.id}`}
          actionId={action.id}
          occupation={occupation}
          zoomLevel={zoomLevel}
          hasError={false}
          maxOrder={maxOrder}
        />
      ))}
    </>
  );
}

const SBDActionMemo = memo(SBDAction);
